/**
 * Early Error Capture Plugin
 *
 * Self-contained plugin that handles errors occurring before Vue/Nuxt/Sentry initialize.
 *
 * **Server-side**: Injects inline script into <head> to capture early errors
 * **Client-side**: Reports captured errors to Sentry with full stack traces
 *
 * This is critical for catching:
 * - ES module import errors ("Importing binding name 'x' is not found")
 * - Chunk loading failures
 * - Script parse errors
 * - Any errors that occur before our error handlers load
 */

// Type declarations for early error capture
interface EarlyError {
  type: 'error' | 'unhandledrejection'
  message?: string
  filename?: string
  lineno?: number
  colno?: number
  error?: {
    name: string
    message: string
    stack?: string
  } | null
  reason?:
    | {
        name: string
        message: string
        stack?: string
      }
    | string
  timestamp: number
  context?: {
    url?: string
    userAgent?: string
    timestamp?: string
  }
  // Enhanced script diagnostics for CORS/script errors
  scriptDiagnostics?: {
    failedScript?: {
      src?: string
      id?: string
      type?: string
      async?: boolean
      defer?: boolean
      crossOrigin?: string | null
    }
    isCorsError?: boolean
    loadedScripts?: Array<{
      src: string
      async: boolean
      defer: boolean
    }>
  }
}

declare global {
  interface Window {
    __earlyErrors?: EarlyError[]
    __earlyErrorsMaxSize?: number
    __earlyErrorsDisabled?: boolean
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // SERVER-SIDE: Inject capture script into HTML
  if (process.server) {
    injectCaptureScript(nuxtApp)
    return
  }

  // CLIENT-SIDE: Report captured errors to Sentry
  reportEarlyErrors(nuxtApp)
})

/**
 * Server-side: Inject early error capture script into <head>
 */

function injectCaptureScript(nuxtApp: any) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Nuxt app hooks are untyped
  nuxtApp.hooks.hook('app:rendered', () => {
    const isDev = process.env.NODE_ENV !== 'production'
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- TODO: Type nuxtApp properly
    const testEarlyError = nuxtApp.ssrContext?.event?.node?.req?.url?.includes(
      'testEarlyError=true',
    )

    const scripts: any[] = [
      {
        id: 'early-error-capture',
        innerHTML: `
(function() {
  // Store for early errors (before Sentry/Vue load)
  window.__earlyErrors = [];
  window.__earlyErrorsMaxSize = 10; // Limit to prevent memory issues

  // Helper to check if error is CORS-related
  function isCorsError(message) {
    if (!message) return false;
    var corsPatterns = [
      'cross-origin',
      'cors',
      'cross origin',
      'access-control-allow-origin',
      'blocked by cors'
    ];
    var lowerMsg = message.toLowerCase();
    return corsPatterns.some(function(p) { return lowerMsg.indexOf(p) !== -1; });
  }

  // Helper to get script diagnostics
  function getScriptDiagnostics(event) {
    var diagnostics = {
      isCorsError: isCorsError(event.message)
    };

    // Check if the event target is a script element (script load error)
    var target = event.target;
    if (target && target.tagName === 'SCRIPT') {
      diagnostics.failedScript = {
        src: target.src || '(inline)',
        id: target.id || null,
        type: target.type || null,
        async: target.async,
        defer: target.defer,
        crossOrigin: target.crossOrigin
      };
    }

    // For CORS errors, capture context about loaded scripts
    if (diagnostics.isCorsError || (target && target.tagName === 'SCRIPT')) {
      var allScripts = document.querySelectorAll('script[src]');
      var loaded = [];

      allScripts.forEach(function(s) {
        loaded.push({
          src: s.src,
          async: s.async,
          defer: s.defer
        });
      });

      diagnostics.loadedScripts = loaded.slice(0, 20); // Limit to avoid huge payloads
    }

    return diagnostics;
  }

  // Capture global errors
  window.addEventListener('error', function(event) {
    // Stop capturing once Vue has loaded and taken over
    if (window.__earlyErrorsDisabled) return;
    if (window.__earlyErrors.length >= window.__earlyErrorsMaxSize) return;

    // Capture additional context for better debugging
    var additionalContext = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };

    // Get script diagnostics for CORS and script load errors
    var scriptDiagnostics = getScriptDiagnostics(event);

    window.__earlyErrors.push({
      type: 'error',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error ? {
        name: event.error.name,
        message: event.error.message,
        stack: event.error.stack
      } : null,
      timestamp: Date.now(),
      context: additionalContext,
      scriptDiagnostics: scriptDiagnostics
    });

    // Prevent the error from propagating to other handlers (like Sentry)
    // to avoid duplicate reporting. We'll report it ourselves when Vue loads.
    event.preventDefault();
  }, true); // Use capture phase to catch before other handlers

  // Helper to get all script info (for context on any error)
  function getAllScriptInfo() {
    var allScripts = document.querySelectorAll('script[src]');
    var scripts = [];
    allScripts.forEach(function(s) {
      if (s.src) {
        scripts.push({
          src: s.src,
          async: s.async,
          defer: s.defer
        });
      }
    });
    return scripts.slice(0, 20); // Limit to avoid huge payloads
  }

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', function(event) {
    // Stop capturing once Vue has loaded and taken over
    if (window.__earlyErrorsDisabled) return;
    if (window.__earlyErrors.length >= window.__earlyErrorsMaxSize) return;

    var reasonMsg = (event.reason && event.reason.message) || String(event.reason);

    window.__earlyErrors.push({
      type: 'unhandledrejection',
      reason: event.reason ? {
        name: event.reason.name,
        message: event.reason.message,
        stack: event.reason.stack
      } : String(event.reason),
      timestamp: Date.now(),
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      scriptDiagnostics: {
        isCorsError: isCorsError(reasonMsg),
        loadedScripts: getAllScriptInfo()
      }
    });

    // Prevent the rejection from propagating to other handlers (like Sentry)
    event.preventDefault();
  });
})();
          `.trim(),
      },
    ]

    // In development, optionally inject a test error script
    if (isDev && testEarlyError) {
      scripts.push({
        id: 'early-error-test',
        innerHTML: `
// TEST MODE: Inject an error before Vue loads
console.warn('[Early Error Test] Injecting test error in 100ms...');
setTimeout(function() {
  var error = new SyntaxError("TEST: Importing binding name 'testBinding' is not found");
  console.error('[Early Error Test] Triggering error:', error.message);
  window.dispatchEvent(new ErrorEvent('error', {
    error: error,
    message: error.message,
    filename: 'test-chunk-abc123.js',
    lineno: 42,
    colno: 10
  }));
}, 100);
        `.trim(),
      })
    }

    useHead({ script: scripts })
  })
}

/**
 * Client-side: Report early errors to Sentry
 */
function reportEarlyErrors(nuxtApp: any) {
  // Check if any errors were captured before this plugin loaded
  if (typeof window === 'undefined' || !Array.isArray(window.__earlyErrors)) {
    return
  }

  const earlyErrors = window.__earlyErrors

  // Disable early error capture now that Vue is loaded
  // We set a flag so the inline script knows to stop capturing
  window.__earlyErrorsDisabled = true

  // Clear the queue (we'll process what's already captured)
  window.__earlyErrors = []

  if (earlyErrors.length === 0) {
    return
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[Early Error Capture] Found ${earlyErrors.length} early error(s) captured before Vue initialized`,
  )

  // Get Sentry from nuxtApp
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type nuxtApp properly
  const sentry = nuxtApp.$sentry

  // Report each early error to Sentry
  earlyErrors.forEach((earlyError, index) => {
    if (earlyError.type === 'error' && earlyError.error) {
      // Create an Error object from the captured data
      const error = new Error(earlyError.error.message || earlyError.message)
      error.name = earlyError.error.name || 'EarlyError'
      if (earlyError.error.stack) {
        error.stack = earlyError.error.stack
      }

      // Extract script diagnostics
      const scriptDiagnostics = earlyError.scriptDiagnostics
      const isCorsError = scriptDiagnostics?.isCorsError ?? false
      const failedScript = scriptDiagnostics?.failedScript

      // Log each error so it appears in Sentry breadcrumbs
      // eslint-disable-next-line no-console
      console.info(
        `[Early Error Capture] Error ${index + 1}/${earlyErrors.length}:`,
        {
          name: error.name,
          message: error.message,
          filename: earlyError.filename,
          lineno: earlyError.lineno,
          colno: earlyError.colno,
          stack: earlyError.error.stack,
          capturedAt: new Date(earlyError.timestamp).toISOString(),
          url: earlyError.context?.url,
          userAgent: earlyError.context?.userAgent,
          isCorsError,
          failedScript: failedScript?.src,
        },
      )

      // Add context about when/where it was captured
      if (sentry) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type sentry properly
        sentry.captureException(error, {
          level: 'error',
          tags: {
            'early-error': true,
            'error-source': 'pre-vue-initialization',
            'error-name': error.name,
            ...(isCorsError && { 'cors-error': true }),
            ...(failedScript && {
              'failed-script-src': failedScript.src?.slice(0, 200),
            }),
          },
          extra: {
            filename: earlyError.filename,
            lineno: earlyError.lineno,
            colno: earlyError.colno,
            capturedAt: new Date(earlyError.timestamp).toISOString(),
            errorIndex: index,
            originalMessage: earlyError.message,
            captureUrl: earlyError.context?.url,
            captureUserAgent: earlyError.context?.userAgent,
            captureTimestamp: earlyError.context?.timestamp,
            // Script diagnostics for CORS/script errors
            scriptDiagnostics: scriptDiagnostics
              ? {
                  isCorsError,
                  failedScript,
                  loadedScriptsCount: scriptDiagnostics.loadedScripts?.length,
                  loadedScripts: scriptDiagnostics.loadedScripts?.slice(0, 10),
                }
              : undefined,
            fullContext: earlyError,
          },
        })
      }
    } else if (earlyError.type === 'unhandledrejection') {
      // Handle promise rejections
      const reasonObj =
        typeof earlyError.reason === 'object' ? earlyError.reason : null
      const error = new Error(reasonObj?.message || String(earlyError.reason))
      error.name = 'EarlyUnhandledRejection'
      if (reasonObj?.stack) {
        error.stack = reasonObj.stack
      }

      // Extract script diagnostics
      const rejectionDiagnostics = earlyError.scriptDiagnostics
      const isRejectionCorsError = rejectionDiagnostics?.isCorsError ?? false

      // Log each rejection so it appears in Sentry breadcrumbs
      // eslint-disable-next-line no-console
      console.info(
        `[Early Error Capture] Rejection ${index + 1}/${earlyErrors.length}:`,
        {
          name: error.name,
          message: error.message,
          reason: reasonObj ?? String(earlyError.reason),
          stack: reasonObj?.stack,
          capturedAt: new Date(earlyError.timestamp).toISOString(),
          isCorsError: isRejectionCorsError,
          url: earlyError.context?.url,
        },
      )

      if (sentry) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type sentry properly
        sentry.captureException(error, {
          level: 'error',
          tags: {
            'early-error': true,
            'error-source': 'pre-vue-initialization',
            'error-type': 'unhandled-rejection',
            ...(isRejectionCorsError && { 'cors-error': true }),
          },
          extra: {
            capturedAt: new Date(earlyError.timestamp).toISOString(),
            errorIndex: index,
            reasonType: typeof earlyError.reason,
            captureUrl: earlyError.context?.url,
            captureUserAgent: earlyError.context?.userAgent,
            captureTimestamp: earlyError.context?.timestamp,
            // Script diagnostics
            scriptDiagnostics: rejectionDiagnostics
              ? {
                  isCorsError: isRejectionCorsError,
                  loadedScriptsCount:
                    rejectionDiagnostics.loadedScripts?.length,
                  loadedScripts: rejectionDiagnostics.loadedScripts?.slice(
                    0,
                    10,
                  ),
                }
              : undefined,
            fullContext: earlyError,
          },
        })
      }
    }
  })

  // eslint-disable-next-line no-console
  console.log(
    `[Early Error Capture] Reported ${earlyErrors.length} early error(s) to Sentry`,
  )
}
